import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getNaturalsAdminApiBase } from "./naturalsSalonAdminApiBase";

const PAYMENT_HOLD_URL = () => `${getNaturalsAdminApiBase()}/nyafaz-ep/licenses/payment-hold`;

const PaymentHoldContext = createContext({ hold: null, paused: false, active: false });

function shouldShowHold(data) {
  return Boolean(data?.paused || data?.renewalGrace);
}

export function usePaymentHold() {
  return useContext(PaymentHoldContext);
}

export function PaymentHoldProvider({ children }) {
  const location = useLocation();
  const [hold, setHold] = useState(null);

  useEffect(() => {
    let active = true;
    const load = () => {
      fetch(PAYMENT_HOLD_URL(), { credentials: "include" })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!active) return;
          setHold(shouldShowHold(data) ? data : null);
        })
        .catch(() => {
          if (active) setHold(null);
        });
    };
    load();
    window.addEventListener("focus", load);
    return () => {
      active = false;
      window.removeEventListener("focus", load);
    };
  }, [location.pathname, location.key]);

  return (
    <PaymentHoldContext.Provider value={{ hold, paused: Boolean(hold?.paused), active: true }}>
      {children}
    </PaymentHoldContext.Provider>
  );
}

export function PaymentRequiredNotice({ portalId = "" }) {
  const { hold } = usePaymentHold();
  if (!hold) return null;
  const canPay = portalId === "workspace" || portalId === "executive";

  if (hold.renewalGrace && !hold.paused) {
    return (
      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
        <strong>Renewal payment reminder.</strong>
        {hold.graceDaysRemaining ? (
          <span className="ml-2 text-xs font-medium uppercase tracking-wide text-[#80489a]">
            {hold.graceDaysRemaining} of {hold.graceDays || 3} day{hold.graceDaysRemaining === 1 ? "" : "s"} left
          </span>
        ) : null}
        <p className="mt-1">{hold.message}</p>
        {hold.shortUrl && canPay ? (
          <a className="mt-3 inline-flex rounded-lg bg-[#80489a] px-3 py-1.5 text-xs font-semibold text-white" href={hold.shortUrl} target="_blank" rel="noreferrer">
            Pay renewal
          </a>
        ) : null}
      </div>
    );
  }

  if (hold.renewalLapsed && !canPay) {
    return (
      <div className="fixed inset-0 z-[96] flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
          <h2 className="text-lg font-semibold text-[#1f1630]">Franchise access paused</h2>
          <p className="mt-2 text-sm text-[#3b3350]">{hold.message}</p>
          <p className="mt-2 text-sm text-[#3b3350]">Dashboard and salon login stay closed. Workspace and executive can complete the payment. Access returns when payment is received.</p>
        </div>
      </div>
    );
  }

  if (hold.renewalLapsed && canPay) {
    return (
      <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
        <strong>Renewal payment required.</strong>
        <p className="mt-1">{hold.message}</p>
        <p className="mt-1">Franchise dashboard and salon login are paused. Pay here to restore them.</p>
        {hold.shortUrl ? (
          <a className="mt-3 inline-flex rounded-lg bg-[#80489a] px-3 py-1.5 text-xs font-semibold text-white" href={hold.shortUrl} target="_blank" rel="noreferrer">
            Pay renewal
          </a>
        ) : null}
      </div>
    );
  }

  if (!hold.paused) return null;
  return (
    <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
      <strong>Complete payment to continue.</strong>
      <p className="mt-1">{hold.message || "Payment is still pending for this organization."}</p>
      <p className="mt-1">Admin access, regions, and HQ admin stay unavailable until payment is confirmed. The menu is locked until then.</p>
      {hold.shortUrl && canPay ? (
        <a className="mt-3 inline-flex rounded-lg bg-[#80489a] px-3 py-1.5 text-xs font-semibold text-white" href={hold.shortUrl} target="_blank" rel="noreferrer">
          Complete payment
        </a>
      ) : null}
    </div>
  );
}

function StandalonePaymentHold({ portalId = "" }) {
  const location = useLocation();
  const [hold, setHold] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(PAYMENT_HOLD_URL(), { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!active) return;
        setHold(shouldShowHold(data) ? data : null);
      })
      .catch(() => {
        if (active) setHold(null);
      });
    return () => {
      active = false;
    };
  }, [location.pathname, location.key]);

  return (
    <PaymentHoldContext.Provider value={{ hold, paused: Boolean(hold?.paused), active: true }}>
      <PaymentRequiredNotice portalId={portalId} />
    </PaymentHoldContext.Provider>
  );
}

export default function PlanPaymentHold({ portalId = "" }) {
  const ctx = usePaymentHold();
  if (!ctx.active) return <StandalonePaymentHold portalId={portalId} />;
  return <PaymentRequiredNotice portalId={portalId} />;
}
